import { useState } from "react";
import { money } from "@/plan-logic/lib/format";

export type Slice = { key: string; label: string; value: number; color: string };

/** The prototype's "Where your money goes" doughnut with its legend (`drawPie`). */
export function Donut({ slices, label }: { slices: Slice[]; label: string }) {
  const [hot, setHot] = useState<number | null>(null);
  const tot = slices.reduce((s, x) => s + x.value, 0);
  if (!tot) return <p className="mut">Nothing goes out in this window of the plan.</p>;
  const R = 150;
  const CX = 170;
  const CY = 170;
  const IN = 84;
  let a0 = -Math.PI / 2;
  const p = (r: number, a: number) => [CX + r * Math.cos(a), CY + r * Math.sin(a)];
  const paths = slices.map((s) => {
    const frac = s.value / tot;
    // A full circle can't be drawn as one arc; nudge it just short.
    const a1 = a0 + Math.min(frac, 0.9999) * Math.PI * 2;
    const big = a1 - a0 > Math.PI ? 1 : 0;
    const [x1, y1] = p(R, a0);
    const [x2, y2] = p(R, a1);
    const [x3, y3] = p(IN, a1);
    const [x4, y4] = p(IN, a0);
    const d = `M${x1} ${y1} A${R} ${R} 0 ${big} 1 ${x2} ${y2} L${x3} ${y3} A${IN} ${IN} 0 ${big} 0 ${x4} ${y4} Z`;
    a0 = a0 + frac * Math.PI * 2;
    return d;
  });
  const pc = (v: number) => `${Math.round((v / tot) * 100)}%`;
  return (
    <div className={`piewrap${hot !== null ? " dim" : ""}`}>
      <div className="piecol">
        <svg viewBox="0 0 340 340" className="pie" role="img" aria-label={label}>
          {slices.map((s, i) => (
            <path
              key={s.key}
              className={`sl${hot === i ? " hot" : ""}`}
              d={paths[i]}
              fill={s.color}
              onMouseEnter={() => setHot(i)}
              onMouseLeave={() => setHot(null)}
            >
              <title>{`${s.label}: ${money(s.value)} a month, ${pc(s.value)}`}</title>
            </path>
          ))}
          <text x="170" y="163" textAnchor="middle" className="pt1">
            {money(tot)}
          </text>
          <text x="170" y="185" textAnchor="middle" className="pt2">
            a month
          </text>
        </svg>
      </div>
      <div className="plegs">
        {slices.map((s, i) => (
          <div
            key={s.key}
            className={`pleg${hot === i ? " hot" : ""}`}
            onMouseEnter={() => setHot(i)}
            onMouseLeave={() => setHot(null)}
          >
            <span className="sw" style={{ background: s.color }} />
            <span className="nm">{s.label}</span>
            <span className="vl tnum">{money(s.value)}</span>
            <span className="pc">{pc(s.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
